import { useCallback } from "react";
import { useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { ACTIVE_WORKSPACE_STORAGE_KEY } from "@/lib/storage-keys";
import { markOperatorExited } from "@/lib/operator-exit";
import { closePlayerSurface } from "@/lib/player-surface";
import { type SignOutOptions, runSignOut } from "@/lib/sign-out-steps";
import { clearPersistedBranding } from "@/lib/workspace-branding-storage";
import { clearWorkspaceAccent } from "@/lib/workspace-branding";

export interface WebSignOutOptions extends SignOutOptions {
  /**
   * Extra teardown that runs BEFORE the session is destroyed, for callers that
   * must revoke a device credential or release a kiosk lease first. Failures
   * are swallowed so a broken revoke cannot block the sign-out.
   */
  beforeSignOut?: () => void | Promise<void>;
}

/**
 * The one operator sign-out for the web app.
 *
 * Mirrors artifacts/mobile/hooks/useSignOut.ts, whose comment explains why a
 * bare signOut() is a cross-account data-exposure rather than a cosmetic bug.
 * Desktop patches the same leak in Electron (CB-148); web was the only
 * unpatched surface.
 *
 * Uses useClerk + useQueryClient only. It must NOT depend on useWorkspace:
 * KioskExitProvider mounts outside WorkspaceProvider, so the workspace id is
 * cleared through localStorage directly, exactly as mobile does.
 */
export function useSignOut(): (options?: WebSignOutOptions) => Promise<void> {
  const { signOut } = useClerk();
  const queryClient = useQueryClient();

  return useCallback(
    (options?: WebSignOutOptions) =>
      runSignOut(
        {
          markExited: markOperatorExited,
          beforeSignOut: options?.beforeSignOut,
          closePlayer: closePlayerSurface,
          clearAccent: clearWorkspaceAccent,
          clearPersistedBranding,
          clearActiveWorkspaceId: () => {
            try {
              localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY);
            } catch {
              // Storage can be unavailable; sign-out must still proceed.
            }
          },
          clearQueryCache: () => queryClient.clear(),
          signOut: (opts) => signOut(opts),
        },
        options,
      ),
    [queryClient, signOut],
  );
}
